/* eslint-disable jsx-a11y/no-static-element-interactions -- onClick handlers on div wrappers for event propagation control */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Button, Drawer, Table, Empty } from 'antd-v5';
import { format, addDays } from 'date-fns';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import { getRequest, postRequest } from '../../../../../core/apiRequests';
import { notificationSuccess } from '../../../../../core/actions';
import ConversationWindow from '../../../../../components/Layout/chat/chat';
import {
  getClientChat,
  resetMessaging,
  setChatReviewLink,
  setClientAssociate,
  setMsgReminderId,
  setMsgReminderDescription,
} from '../../../../../reducers/messaging';
import { resetToday } from '../../../../../reducers/today';
import getClientPaymentStatus from '../../../../../components/Layout/GetClientPaymentStatus';
import { sourceCalc } from '../../../../../components/Routes/Today/children/logicFunctions';
import { associateNameFormat } from '../../../../../utils';
import SnoozeModal from '../../../../../components/Routes/Today/Reminders/children/SnoozeModal';
import ReminderModal from '../../../../../components/Layout/Modals/EditReminderModal';
import PopConfirm from '../../../../../components/Layout/PopConfirm';
import DropDownSelect from '../../../../../components/Layout/DropdownSelect';
import MessageConfigToday from '../../../../../components/Routes/Today/children/MessageConfigToday';
import '../../../../../components/Routes/Today/today.css';

const AllReminders = ({ auth }) => {
  const [showChat, setShowChat] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isShown, setIsShown] = useState('');
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [editReminderModal, setEditReminderModal] = useState(false);
  const [checkCustomReminder, setCheckCustomReminder] = useState(false);
  const [assignPopup, setAssignPopup] = useState(false);
  const [dismissPopup, setDismissPopup] = useState(false);
  const [dismissAllPopup, setDismissAllPopup] = useState(false);
  const [hasOverdueReminders, setHasOverdueReminders] = useState(false);
  const [associate, setAssociate] = useState();
  const [allAssoc, setAllAssoc] = useState([]);

  const today = format(new Date(), 'yyyyMMdd');
  const tomorrowNoFormat = addDays(new Date(), 1);
  const tomorrow = format(tomorrowNoFormat, 'yyyyMMdd');

  useEffect(() => {
    if (selectedRows.length === 1) {
      const [action] = selectedRows;
      if (
        !action.life_event_id &&
        !action.tag_action_config_id &&
        !action.sales_opp_id &&
        !action.custom_event_type_id &&
        !action.sales_action_config_id
      ) {
        setCheckCustomReminder(true);
      } else {
        setCheckCustomReminder(false);
      }
    } else {
      setCheckCustomReminder(false);
    }
  }, [selectedRows]);

  // Fetch associates for reassignment
  useEffect(() => {
    if (auth.merchantId) {
      const fetchAssociates = async () => {
        const reqObj = {
          params: ['report', auth.merchantId, auth.storeId || 0, 'clientByAction'],
          query: {
            userId: auth.userId,
            role: auth.role,
          },
        };
        const data = await getRequest(reqObj);
        const sortedAssocs = data?.sort((a, b) => {
          const nameA = `${a.first_name || ''}${a.last_name || ''}`;
          const nameB = `${b.first_name || ''}${b.last_name || ''}`;
          return nameA.localeCompare(nameB);
        });
        setAllAssoc(sortedAssocs || []);
        setAssociate(auth.userId);
      };
      fetchAssociates();
    }
  }, [auth.merchantId, auth.storeId, auth.userId, auth.role]);

  const {
    data: reminders,
    isFetching,
    refetch,
  } = useQuery(
    ['allReminders', page, pageSize],
    async () => {
      try {
        const reqObj = {
          params: ['today', auth.merchantId, 'getReminders', auth.userId],
          query: {
            pageStart: page,
            count: pageSize,
            sortColumn: 'due_date',
            ascending: true,
          },
        };
        const response = await getRequest(reqObj);
        if (response?.data && response?.pageInfo) {
          return {
            data: response.data,
            total: response.pageInfo.rowCount,
          };
        }
        return { data: [], total: 0 };
      } catch (error) {
        console.error('Error fetching reminders:', error);
        return { data: [], total: 0 };
      }
    },
    {
      refetchOnWindowFocus: false,
      initialData: { data: [], total: 0 },
      enabled: !!auth?.merchantId,
    },
  );

  // Check if there are any overdue reminders
  useEffect(() => {
    if (reminders?.data?.length > 0) {
      const now = moment(new Date());
      const hasOverdue = reminders.data.some(
        reminder => !reminder.complete_date && moment(now).isAfter(moment(reminder.due_date, 'YYYYMMDD').add(1, 'day')),
      );
      setHasOverdueReminders(hasOverdue);
    } else {
      setHasOverdueReminders(false);
    }
  }, [reminders?.data]);

  const expiresOn = (todayDate, expiresOnDate) => {
    const a = moment(todayDate, 'YYYYMMDD');
    const b = moment(expiresOnDate, 'YYYYMMDD');
    return b.diff(a, 'days');
  };

  const closeChat = () => {
    setShowChat(false);
    resetMessaging();
    resetToday();
  };

  const getReviewLink = async reviewStoreId => {
    if (reviewStoreId) {
      const reqObj = { params: ['chats', reviewStoreId, 'getReviewLink'] };
      const linkData = await getRequest(reqObj);
      if (linkData.length > 0) {
        setChatReviewLink(linkData[0].review_link);
      }
    }
  };

  const setSelectedClient = async client => {
    await getClientChat(client);
    await getReviewLink(client.store_id);
    setClientAssociate(client.associateCount);
    getClientPaymentStatus({ client, auth });
    setShowChat(true);
    setMsgReminderId(client.id);
    setMsgReminderDescription(client.description);
  };

  const saveSelected = async () => {
    const reqObj = {
      params: ['today', auth.merchantId, 'save'],
      data: { selected: selectedRows },
      query: {
        storeId: auth.storeId,
        associateId: auth.userId,
        createdBy: `${auth.firstName || ''} ${auth.lastName || ''}`,
      },
    };
    const res = await postRequest(reqObj);
    if (res) {
      setSelectedRows([]);
      setSelectedRowKeys([]);
      refetch();
      notificationSuccess('Reminder(s) Complete!');
    }
  };

  const assignAssociate = async () => {
    setAssignPopup(false);
    const reqObj = {
      params: ['today', auth.merchantId, 'assignAssociate'],
      data: { selectedAction: selectedRows },
      query: {
        assignedAssociate: associate,
        storeId: auth.storeId,
        associateId: auth.userId,
      },
    };
    const res = await postRequest(reqObj);
    if (res) {
      setSelectedRows([]);
      setSelectedRowKeys([]);
      refetch();
      notificationSuccess('Reminder(s) Assigned!');
      setAssociate(auth.userId);
    }
  };

  const dismissSelected = async () => {
    const reqObj = {
      params: ['today', auth.merchantId, 'dismissSelected'],
      query: {
        associateName: associateNameFormat(auth.firstName, auth.lastName),
        associateId: auth.userId,
      },
      data: { selectedAction: selectedRows },
    };
    const responseObj = await postRequest(reqObj);
    if (responseObj) {
      setSelectedRows([]);
      setSelectedRowKeys([]);
      setDismissPopup(false);
      refetch();
      notificationSuccess('Reminder(s) Dismissed!');
    }
  };

  const dismissAllOverdue = async () => {
    const reqObj = {
      params: ['today', auth.merchantId, 'dismissAll'],
      data: {
        associateName: associateNameFormat(auth.firstName, auth.lastName),
        associateId: auth.userId,
      },
    };
    const responseObj = await postRequest(reqObj);
    if (responseObj) {
      setDismissAllPopup(false);
      refetch();
      notificationSuccess('Reminder(s) Dismissed!');
    }
  };

  const rowSelection = {
    selectedRowKeys,
    columnWidth: 48,
    onChange: (newSelectedRowKeys, newSelectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedRows(newSelectedRows);
    },
  };

  const columns = useMemo(
    () => [
      {
        title: 'Due Date',
        dataIndex: 'due_date',
        key: 'due_date',
        width: '15%',
        render: (t, record) => {
          if (t) {
            if (parseInt(today, 10) === record.expires_on_date) {
              return (
                <div className="flex-col-left">
                  {moment(t, 'YYYYMMDD').format('MM/DD/YYYY')}
                  <span className="fs-12 fw-400 gray">Expires today</span>
                </div>
              );
            }
            if (parseInt(tomorrow, 10) === record.expires_on_date) {
              return (
                <div className="flex-col-left">
                  {moment(t, 'YYYYMMDD').format('MM/DD/YYYY')}
                  <span className="fs-12 fw-400 gray">Expires tomorrow</span>
                </div>
              );
            }
            if (record.expires_on_date) {
              return (
                <div className="flex-col-left">
                  {moment(t, 'YYYYMMDD').format('MM/DD/YYYY')}
                  <span className="fs-12 fw-400 gray">Expires in {expiresOn(today, record.expires_on_date)} days</span>
                </div>
              );
            }
            return moment(t, 'YYYYMMDD').format('MM/DD/YYYY');
          }
          return null;
        },
      },
      {
        title: 'Client Name',
        dataIndex: 'name',
        key: 'name',
        width: '15%',
        render: (text, record) => (
          <Link to={`/Clients?client=${record.client_id}`} onClick={e => e.stopPropagation()}>
            {text}
          </Link>
        ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: '25%',
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        width: '15%',
        render: (_, record) => sourceCalc(record),
      },
      {
        title: 'Reassign',
        dataIndex: 'reassigned_by',
        key: 'reassigned_by',
        width: '8%',
        align: 'center',
        render: () => (
          <div className="align-center" style={{ maxHeight: '34px' }}>
            <FontAwesomeIcon
              icon={['far', 'pencil']}
              onClick={e => {
                e.stopPropagation();
                setAssignPopup(true);
              }}
              className="pointer gray"
            />
          </div>
        ),
      },
      {
        title: 'Message',
        dataIndex: 'message',
        key: 'message',
        width: '10%',
        align: 'center',
        render: (_, record) => (
          <div className="align-center pos-rel" style={{ maxHeight: '34px' }} onClick={e => e.stopPropagation()}>
            <MessageConfigToday
              keyVar={Math.floor(Math.random() * record.id)}
              msgObj={record}
              setShowPopConfirm={setSelectedClient}
              initiateTemplates={setSelectedClient}
              setIsShown={setIsShown}
              isShown={isShown}
            />
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: '12%',
        align: 'center',
        render: (_, record) => {
          const now = moment(new Date());
          if (!record.complete_date) {
            if (moment(now).isAfter(moment(record.due_date, 'YYYYMMDD').add(1, 'day'))) {
              return <span className="today-status-badge overdue">OVERDUE</span>;
            }
            return <span className="today-status-badge active">ACTIVE</span>;
          }
          return null;
        },
      },
    ],
    [today, tomorrow, isShown],
  );

  return (
    <StandardLayoutV2
      title="Reminders"
      breadcrumb={[{ path: '/', title: 'Today' }, { title: 'Reminders' }]}
      classProps="flex-col-left"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingTop: '0px' }}
      actions={
        selectedRowKeys.length > 0 ? (
          <div className="cb-link-btn-group cb-link-btn-group--wide">
            {checkCustomReminder && (
              <Button type="link" className="cb-link-btn-action" onClick={() => setEditReminderModal(true)}>
                <FontAwesomeIcon icon={['far', 'pencil']} />
                Edit
              </Button>
            )}
            <Button type="link" className="cb-link-btn-action" onClick={() => setAssignPopup(true)}>
              <FontAwesomeIcon icon={['far', 'user']} />
              Assign
            </Button>
            <Button type="link" className="cb-link-btn-action cb-link-btn-action--icon-lg" onClick={saveSelected}>
              <FontAwesomeIcon icon={['far', 'check-circle']} />
              Complete
            </Button>
            <Button type="link" className="cb-link-btn-action" onClick={() => setDismissPopup(true)}>
              <FontAwesomeIcon icon={['far', 'eraser']} />
              Dismiss
            </Button>
            <Button type="link" className="cb-link-btn-action" onClick={() => setShowSnoozeModal(true)}>
              <FontAwesomeIcon icon="fa-sharp fa-regular fa-alarm-clock" />
              Snooze
            </Button>
          </div>
        ) : (
          <div className="cb-link-btn-group">
            <Button
              type="link"
              className="cb-link-btn-action cb-link-btn-action--icon-lg"
              onClick={() => setDismissAllPopup(true)}
              disabled={!hasOverdueReminders}
            >
              <FontAwesomeIcon icon={['far', 'eraser']} />
              Dismiss All Overdue
            </Button>
          </div>
        )
      }
    >
      <Table
        className="today-table"
        columns={columns}
        dataSource={reminders?.data || []}
        rowKey={record => record.id}
        loading={isFetching}
        rowSelection={rowSelection}
        pagination={{
          pageSize,
          current: page,
          total: reminders?.total || 0,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
          },
          showSizeChanger: true,
        }}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Reminders" />,
        }}
      />

      {editReminderModal && (
        <ReminderModal
          open={editReminderModal}
          reminder={selectedRows?.[0] ?? {}}
          allAssoc={allAssoc}
          onClose={() => setEditReminderModal(false)}
          setReload={() => {
            setSelectedRows([]);
            setSelectedRowKeys([]);
            refetch();
          }}
        />
      )}

      <SnoozeModal
        openModal={showSnoozeModal}
        setOpenModal={setShowSnoozeModal}
        merchantId={auth.merchantId}
        actions={selectedRows}
        refresh={() => {
          setSelectedRows([]);
          setSelectedRowKeys([]);
          refetch();
        }}
      />

      {assignPopup && (
        <PopConfirm
          popStyle={{ width: '380px' }}
          confirmButtonText="Save"
          cancelButtonText="Cancel"
          confirm="Assign Associate"
          description={[
            <div key="primary" className="align-left m-top-10" style={{ height: '175px' }}>
              <span className="dropDownDescriptionText">Assign an Associate</span>
              <DropDownSelect
                classProps="mq-w-200 w-295 align-left m-top-8"
                options={allAssoc.map(asc => ({
                  label: asc.fullName ? asc.fullName : `${asc.first_name || ''} ${asc.last_name || ''}`,
                  value: asc.id,
                }))}
                placeholder="Select an Associate..."
                value={associate}
                onChange={obj => {
                  setAssociate(obj.value);
                }}
              />
            </div>,
          ]}
          noButtonDisable
          onCancel={() => setAssignPopup(false)}
          onConfirm={assignAssociate}
        />
      )}

      {dismissPopup && (
        <PopConfirm
          popStyle={{ width: '380px' }}
          customHeaderContent="fs-16 align-center"
          confirmButtonText="Dismiss"
          cancelButtonText="Cancel"
          confirm="Dismiss Selected Reminders"
          description="These reminders will be dismissed from your list. If you continue, these reminders will be permanently removed from your profile."
          noButtonDisable
          onCancel={() => setDismissPopup(false)}
          onConfirm={dismissSelected}
        />
      )}

      {dismissAllPopup && (
        <PopConfirm
          popStyle={{ width: '380px' }}
          customHeaderContent="fs-16 align-center"
          confirmButtonText="Dismiss"
          cancelButtonText="Cancel"
          confirm="Dismiss All Overdue Reminders"
          description="All overdue reminders will be removed from your list. If you continue, these reminders will be permanently removed from your profile."
          noButtonDisable
          onCancel={() => setDismissAllPopup(false)}
          onConfirm={dismissAllOverdue}
        />
      )}

      <Drawer
        styles={{
          body: { padding: 0, overflow: 'hidden' },
        }}
        closable={false}
        push={false}
        destroyOnClose
        width={750}
        open={showChat}
        onClose={closeChat}
      >
        <div style={{ height: '100vh' }}>
          <ConversationWindow setDrawerToggle={closeChat} chatIconColor="#EBEBEB" type="right" />
        </div>
      </Drawer>
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AllReminders);
